import React, { Suspense } from 'react';
import PropTypes from 'prop-types';

import i18n from '../../i18n';
import Payment from '../../api/Payment';
import User from '../../models/User';

const CreditCardFormFields = React.lazy(() => import('./CreditCardFormFields'));

// Map our fields with Stripe's
const creditCardAdditionalFieldsMapping = {
    'card-name': 'name',
    'card-address': 'address_line1',
    'card-zipcode': 'address_zip',
    'card-city': 'address_city',
    'card-country': 'address_country'
};
const creditCardAdditionalFields = Object.keys(creditCardAdditionalFieldsMapping);

const withPaymentFormTools = (WrappedComponent) =>
    class extends React.PureComponent {
        static propTypes = {
            user: PropTypes.instanceOf(User).isRequired
        };

        errors = {};

        constructor(props) {
            super(props);

            this.state = {
                errorMessage: null,
                formErrors: {},
                stripeElements: []
            };

            this.addStripeElement = this.addStripeElement.bind(this);
            this.setError = this.setError.bind(this);
            this.setFormError = this.setFormError.bind(this);
            this.handleError = this.handleError.bind(this);
            this.handleApiError = this.handleApiError.bind(this);
            this.handlePaypalError = this.handlePaypalError.bind(this);
            this.createAndConfirmSetupIntent = this.createAndConfirmSetupIntent.bind(this);
            this.createPaymentIntent = this.createPaymentIntent.bind(this);
            this.confirmPaymentIntent = this.confirmPaymentIntent.bind(this);
            this.onCreditCardFormFieldChange = this.onCreditCardFormFieldChange.bind(this);
            this.onCustomFieldChange = this.onCustomFieldChange.bind(this);
            this.onCustomFieldChangeThrottled = (e) => _.throttle(this.onCustomFieldChange(e), 200);
            this.validateField = this.validateField.bind(this);
            this.validateFields = this.validateFields.bind(this);
            this.listFormErrors = this.listFormErrors.bind(this);
            this.validateCreditCardForm = this.validateCreditCardForm.bind(this);
            this.clearFormErrors = this.clearFormErrors.bind(this);
            this.clearCreditCardForm = this.clearCreditCardForm.bind(this);
            this.getStripeAdditionalFields = this.getStripeAdditionalFields.bind(this);
            this.scrollToFirstError = this.scrollToFirstError.bind(this);

            this.creditCardFormFields = React.createRef();
        }

        addStripeElement(element) {
            this.setState({
                stripeElements: [...this.state.stripeElements, element]
            });
        }

        setError(error) {
            this.setState({
                errorMessage: error
            });
        }

        setFormError(property, error) {
            if (!error) {
                delete this.errors[property];
            } else {
                this.errors[property] = error;
            }
            this.setState({
                formErrors: Object.assign({}, this.errors)
            });
        }

        clearFormErrors() {
            this.setState({
                formErrors: {}
            });
            this.errors = {};
        }

        handleError(e) {
            if (e.type && e.type === 'validation_error') {
                // Validation errors shouldn't display a banner
                return;
            }

            let message;
            if (e.message && i18n.exists([`billing:${e.type}.${e.code}`, e.message])) {
                message = i18n.t([`billing:${e.type}.${e.code}`, e.message]);
            } else if (this.getStripeErrorMessage(e)) {
                message = this.getStripeErrorMessage(e);
            } else if (e.message) {
                message = e.message;
            } else {
                message = i18n.t('billing:error.generic-message', {
                    contact_start: `<a href="${i18n.t('url:contact_us_he')}">`,
                    contact_end: '</a>'
                });
            }

            this.setError(message);
        }

        getStripeErrorMessage(errorDetails) {
            let errorMessage = '';

            switch (errorDetails.code) {
                case 'card_declined':
                    if (errorDetails.decline_code === 'incorrect_zip') {
                        this.setFormError('card-zipcode', i18n.t('billing:error.incorrect-zip'));
                    }
                    errorMessage = i18n.t('billing:error.card-declined');
                    break;
                case 'card_already_exists':
                    errorMessage = i18n.t('billing:error.card-exists');
                    break;
                case 'expired_card':
                    errorMessage = i18n.t('billing:error.expired-card');
                    break;
                case 'incorrect_number':
                case 'invalid_number':
                    errorMessage = i18n.t('billing:error.invalid-number');
                    break;
                case 'invalid_expiry_year':
                case 'invalid_expiry_month':
                    errorMessage = i18n.t('billing:error.invalid-expiry');
                    break;
                case 'incorrect_cvc':
                case 'invalid_cvc':
                    errorMessage = i18n.t('billing:error.invalid-cvc');
                    break;
                case 'incorrect_address':
                    this.setFormError('card-address', i18n.t('billing:error.incorrect-address'));
                    break;
                case 'incorrect_zip':
                    this.setFormError('card-zipcode', i18n.t('billing:error.incorrect-zip'));
                    break;
                case 'setup_intent_authentication_failure':
                    errorMessage = i18n.t('billing:payment-method.addmethod.error.calloutBody', {
                        link: i18n.t('url:contact_us')
                    });
                    break;
                default:
                    errorMessage = '';
                    break;
            }

            return errorMessage;
        }

        getApiErrorMessage(errorDetails) {
            let errorMessage = '';

            switch (errorDetails.code) {
                case 'page.payment.subscribe.error.already_subscriber':
                    errorMessage = i18n.t('billing:page.payment.subscribe.error.already_subscriber', {
                        contact_start: `<a href="${i18n.t('url:contact_us_he')}">`,
                        contact_end: '</a>'
                    });
                    break;
                default:
                    errorMessage = '';
                    break;
            }

            return errorMessage;
        }

        handleApiError(payload) {
            let errorMessage;
            if (payload && payload.error) {
                let err = JSON.parse(JSON.stringify(payload.error));
                if (err && err.error) {
                    err = err.error;
                }

                if (payload.code && err.details) {
                    if ((payload.code == 402 || payload.code == 409) && err.details.code) {
                        errorMessage =
                            this.getStripeErrorMessage(err.details) || this.getApiErrorMessage(err.details);
                    } else if (payload.code == 409 && err.details.unlocks_in) {
                        errorMessage = i18n.t('billing:error.payment-locked');
                    }
                }
            }

            this.handleError(new Error(errorMessage));
        }

        handlePaypalError(err) {
            let message;
            if (err === 'payment-cancelled') {
                message = i18n.t('billing:error.paypal-cancelled');
            } else if (err === 'payment-error') {
                message = i18n.t('billing:error.paypal-error');
            } else if (err === 'component-creation-error') {
                message = i18n.t('billing:error.paypal-component-error');
            } else {
                message = i18n.t('billing:error.generic-message', {
                    contact_start: `<a href="${i18n.t('url:contact_us_he')}">`,
                    contact_end: '</a>'
                });
            }

            this.setError(message);
        }

        async createAndConfirmSetupIntent(stripe, form) {
            if (!stripe) {
                throw new Error();
            }

            const stripeAdditionalFields = this.getStripeAdditionalFields(form);
            const setupIntent = await Payment.createStripeSetupIntent();

            const result = await stripe.confirmCardSetup(setupIntent.client_secret, {
                payment_method: {
                    card: this.state.stripeElements[0],
                    billing_details: {
                        name: stripeAdditionalFields['name'],
                        email: this.props.user ? this.props.user.get('email') : undefined,
                        phone: this.props.user ? this.props.user.get('phone') : undefined,
                        address: {
                            line1: stripeAdditionalFields['address_line1'],
                            city: stripeAdditionalFields['address_city'],
                            postal_code: stripeAdditionalFields['address_zip'],
                            country: stripeAdditionalFields['address_country']
                        }
                    }
                }
            });

            if (result.error) {
                throw result.error;
            }
            return result.setupIntent.id;
        }

        async createPaymentIntent(paymentContext) {
            try {
                return await Payment.createStripePaymentIntent(paymentContext);
            } catch (error) {
                const apiError = {
                    payload: {
                        error: error.responseJSON,
                        code: error.status
                    }
                };
                throw apiError;
            }
        }

        async confirmPaymentIntent(stripe, form, paymentContext, paymentIntent) {
            if (!stripe) {
                throw new Error();
            }
            const stripeAdditionalFields = !paymentContext.card_id
                ? this.getStripeAdditionalFields(form)
                : [];

            const result = await stripe.confirmCardPayment(paymentIntent.client_secret, {
                payment_method: paymentContext.card_id
                    ? paymentContext.card_id
                    : {
                          card: this.state.stripeElements[0],
                          billing_details: {
                              name: stripeAdditionalFields['name'],
                              email: this.props.user ? this.props.user.get('email') : undefined,
                              phone: this.props.user ? this.props.user.get('phone') : undefined,
                              address: {
                                  line1: stripeAdditionalFields['address_line1'],
                                  city: stripeAdditionalFields['address_city'],
                                  postal_code: stripeAdditionalFields['address_zip'],
                                  country: stripeAdditionalFields['address_country']
                              }
                          }
                      }
            });
            if (result.error) {
                throw result.error;
            }

            return result.paymentIntent.id;
        }

        getStripeAdditionalFields(form) {
            const stripeAdditionalFields = {};
            Array.prototype.slice
                .call(form.current.elements)
                .filter((input) => creditCardAdditionalFields.includes(input.name))
                .forEach((input) => {
                    stripeAdditionalFields[creditCardAdditionalFieldsMapping[input.name]] = input.value;
                });

            return stripeAdditionalFields;
        }

        onCreditCardFormFieldChange(fieldName) {
            return (e) => {
                if (e.error && e.error.message) {
                    this.setFormError(
                        fieldName,
                        i18n.t([`billing:${e.error.type}.${e.error.code}`, e.error.message])
                    );
                } else {
                    this.setFormError(fieldName, null);
                }
            };
        }

        onCustomFieldChange(fieldName) {
            return (e) => {
                this.validateField(fieldName, e.target.value);
            };
        }

        validateField(fieldName, fieldValue) {
            if (fieldValue.length === 0) {
                this.setFormError(
                    fieldName,
                    i18n.t([`billing:validation_error.${fieldName}.empty`, 'common:validation_error.empty'])
                );
            } else {
                this.setFormError(fieldName, null);
            }
        }

        validateFields(form) {
            // JS validation
            Array.prototype.slice
                .call(form.current.elements)
                .filter((input) => creditCardAdditionalFields.includes(input.name))
                .forEach((input) => {
                    this.validateField(input.name, input.value);
                });

            return this.errors;
        }

        async validateStripe(stripe) {
            // Stripe doesn't provide validation functions, we need to try to create a token
            const res = await stripe.createToken();
            if (res.error) {
                throw res.error;
            }

            return res.token;
        }

        listFormErrors(form) {
            // HTML5 validation
            Array.prototype.slice
                .call(form.current.elements)
                .filter((input) => input.checkValidity && !input.checkValidity())
                .forEach((input) => {
                    const message = input.validationMessage;
                    this.setFormError(input.name, message);
                });

            return this.errors;
        }

        async validateCreditCardForm(stripe, form) {
            // Validate fields
            this.listFormErrors(form);
            this.validateFields(form);
            const token = await this.validateStripe(stripe);
            if (Object.keys(this.errors).length > 0) {
                throw this.errors;
            }

            return token;
        }

        scrollToFirstError() {
            const firstErrorElement =
                document.querySelector('.payment-details .error') || document.querySelector('.payment-error');
            if (!firstErrorElement) {
                return;
            }

            const rect = firstErrorElement.getBoundingClientRect();
            const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
            if (!isVisible) {
                firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }

        clearCreditCardForm(form) {
            const { stripeElements } = this.state;

            // Clear additional fields
            if (form && form.current) {
                form.current.reset();
            }

            // Clear select
            if (this.creditCardFormFields && this.creditCardFormFields.current) {
                if (
                    this.creditCardFormFields.current.countrySelect &&
                    this.creditCardFormFields.current.countrySelect.current &&
                    this.creditCardFormFields.current.countrySelect.current.select
                ) {
                    this.creditCardFormFields.current.countrySelect.current.select.clearValue();
                }
            }

            // Clear Stripe fields
            try {
                stripeElements.forEach((stripeElement) => {
                    stripeElement.clear();
                });
            } catch (error) {
                // Do nothing
            }
        }

        render() {
            const { errorMessage, formErrors, stripeElements } = this.state;

            const fields = (
                <Suspense fallback={<div>{`${i18n.t('common:loading')}...`}</div>}>
                    <CreditCardFormFields
                        ref={this.creditCardFormFields}
                        errors={formErrors}
                        onFieldChange={this.onCreditCardFormFieldChange}
                        onCustomFieldChange={this.onCustomFieldChangeThrottled}
                        addStripeElement={this.addStripeElement}
                    />
                </Suspense>
            );

            return (
                <WrappedComponent
                    {...this.props}
                    fields={fields}
                    stripeElements={stripeElements}
                    validateCreditCardForm={this.validateCreditCardForm}
                    createAndConfirmSetupIntent={this.createAndConfirmSetupIntent}
                    createPaymentIntent={this.createPaymentIntent}
                    confirmPaymentIntent={this.confirmPaymentIntent}
                    clearCreditCardForm={this.clearCreditCardForm}
                    errorHandling={{
                        errorMessage,
                        formErrors,
                        listFormErrors: this.listFormErrors,
                        setFormError: this.setFormError,
                        clearFormErrors: this.clearFormErrors,
                        setError: this.setError,
                        handleError: this.handleError,
                        handleApiError: this.handleApiError,
                        handlePaypalError: this.handlePaypalError,
                        scrollToFirstError: this.scrollToFirstError
                    }}
                />
            );
        }
    };

export default withPaymentFormTools;
